import ContentfulRichText from "components/content/ContentfulRichText";
import { ContentHeader } from "components/content/ContentHeader";
import createContentfulClient from "components/content/graphql/apollo/createContentfulClient";
import type { IFundamenteiBlogFieldsFragment } from "components/content/graphql/fragments/__generated__/FundamenteiBlogFields.generated";
import type { IFundamenteiBlogPostFieldsFragment } from "components/content/graphql/fragments/__generated__/FundamenteiBlogPostFields.generated";
import type {
  IFundamenteiBlogCollectionQuery,
  IFundamenteiBlogCollectionQueryVariables,
} from "components/content/graphql/queries/__generated__/FundamenteiBlogCollectionQuery.generated";
import type {
  IFundamenteiBlogPostQuery,
  IFundamenteiBlogPostQueryVariables,
} from "components/content/graphql/queries/__generated__/FundamenteiBlogPostQuery.generated";
import FundamenteiBlogCollectionQuery from "components/content/graphql/queries/FundamenteiBlogCollectionQuery";
import FundamenteiBlogPostQuery from "components/content/graphql/queries/FundamenteiBlogPostQuery";
import type { IFundamenteiBlogFilter, InputMaybe } from "components/content/graphql/types/graphqlTypes";
import { IFundamenteiBlogOrder } from "components/content/graphql/types/graphqlTypes";
import LatestArticles from "components/content/LatestArticles";
import NavbarLayout from "components/layouts/NavbarLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "components/ui/Breadcrumb";
import parseISO from "date-fns/parseISO";
import SetHttpStatusCode from "lib/http/SetHttpStatusCode";
import getBaseUrl from "lib/utils/getBaseUrl";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { SlashIcon } from "lucide-react";
import type { GetServerSideProps } from "next";
import { ArticleJsonLd } from "next-seo";
import Link from "next/link";
import NotFound from "pages/404";
import { FormattedMessage } from "react-intl";

interface ArticlePageBySlugProps {
  article: IFundamenteiBlogPostFieldsFragment | null;
  mostRecentArticles: IFundamenteiBlogFieldsFragment[];
}

export default function ArticlePageBySlug({ article, mostRecentArticles }: ArticlePageBySlugProps) {
  if (!article || !article.title || !article.body || !article.slug) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  const author = article.authorsCollection?.items[0];

  return (
    <NavbarLayout>
      <FundamenteiMetadata
        title={`${article.title} | Fundamentei`}
        description={article.description || undefined}
        openGraph={{
          images:
            article.cover && article.cover.url
              ? [
                  {
                    url: article.cover.url,
                    alt: article.cover.description || article.cover.title || "",
                  },
                ]
              : undefined,
        }}
      />

      {article.description && (
        <ArticleJsonLd
          type="NewsArticle"
          title={article.title}
          description={article.description}
          url={`${getBaseUrl()}/blog/${article.slug}`}
          datePublished={article.sys.firstPublishedAt}
          dateModified={article.sys.publishedAt}
          isAccessibleForFree={true}
          images={article.cover && article.cover.url ? [article.cover.url] : []}
          authorName={author ? author.name || "Fundamentei" : "Fundamentei"}
          publisherName="Fundamentei"
          publisherLogo={`${getBaseUrl()}/static/og/1200w.png?v=20230529`}
        />
      )}

      <div className="mx-auto my-8 flex max-w-screen-xl flex-col px-4 lg:flex-row lg:space-x-10 lg:px-4">
        <div className="flex flex-col space-y-6 lg:max-w-3xl">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild={true} href="/">
                  <Link href="/" className="hover:underline">
                    Home
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <SlashIcon />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbLink asChild={true} href="/blog">
                  <Link href="/blog" className="hover:underline">
                    <FormattedMessage defaultMessage="Artigos" id="7UdFxy" />
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {author && (
            <ContentHeader
              slug={article.slug}
              title={article.title}
              author={{
                name: author.name || "Fundamentei",
                imageUrl: author.image?.url || `${getBaseUrl()}/static/branding/symbol/White on Blue@4x.jpg`,
              }}
              firstPublishedAt={parseISO(article.sys.firstPublishedAt)}
              lastPublishedAt={article.sys.publishedAt ? parseISO(article.sys.publishedAt) : null}
            />
          )}

          {article.cover && article.cover.url && (
            <figure className="mb-6 flex flex-col items-center gap-2 rounded-xl">
              <img
                alt={article.cover.description || article.cover.title || ""}
                loading="lazy"
                width={300}
                src={article.cover.url}
                className="aspect-video max-h-96 w-full rounded-xl object-cover object-top"
              />
              {article.cover.description && (
                <figcaption className="break-all text-center text-sm italic text-zinc-600">
                  {article.cover.description}
                </figcaption>
              )}
            </figure>
          )}

          <ContentfulRichText content={article.body} />
        </div>

        {mostRecentArticles.length > 0 && (
          <div className="flex w-full max-w-3xl flex-col space-y-6 lg:sticky lg:top-navbar-desktop-offset">
            <LatestArticles articles={mostRecentArticles} />
          </div>
        )}
      </div>
    </NavbarLayout>
  );
}

export const getServerSideProps: GetServerSideProps<
  any,
  {
    slug: string;
  }
> = async ({ params, query }) => {
  const isPreview = query.preview === "true";

  if (!params || !params.slug) {
    return {
      props: {
        article: null,
        mostRecentArticles: [],
      },
    };
  }

  const client = createContentfulClient();

  const [{ data }, { data: mostRecentArticles }] = await Promise.all([
    client.query<IFundamenteiBlogPostQuery, IFundamenteiBlogPostQueryVariables>({
      query: FundamenteiBlogPostQuery,
      variables: {
        slug: params.slug,
        locale: "pt-BR",
        preview: isPreview,
      },
    }),

    client.query<IFundamenteiBlogCollectionQuery, IFundamenteiBlogCollectionQueryVariables>({
      query: FundamenteiBlogCollectionQuery,
      variables: {
        limit: 5,
        locale: "pt-BR",
        order: [IFundamenteiBlogOrder.SysFirstPublishedAtDesc],
        skip: null,
        // Filter by articles that are not the one being viewed
        where: {
          slug_not: params.slug,
        } as InputMaybe<IFundamenteiBlogFilter>,
        preview: false,
      },
    }),
  ]);

  const article =
    data.fundamenteiBlogCollection &&
    data.fundamenteiBlogCollection.items &&
    data.fundamenteiBlogCollection.items.length > 0
      ? data.fundamenteiBlogCollection.items[0]
      : null;

  return {
    props: {
      article,
      mostRecentArticles: mostRecentArticles.fundamenteiBlogCollection?.items.filter(notUndefinedOrNull) || [],
    },
  };
};
